/**
 * MH-17 Lịch tuần — `/lich-phat/tuan` (mục 6.1 đặc tả giao diện): trục ngang bảy
 * ngày, trục dọc giờ 4h–22h. Mỗi khối là một lần phát cụ thể (một `ThoiDiemPhat`
 * của một lịch, máy chủ trả `danhSachNgayPhat` đã tính sẵn, giao diện chỉ lọc đúng
 * bảy ngày của tuần hiện tại, không tự suy diễn quy tắc lặp). Dạng danh sách (lọc,
 * phân trang, hủy lịch) nằm ở trang `/lich-phat` (`DanhSachLichPhat.tsx`); màn hình
 * này chỉ đặt liên kết sang đó.
 *
 * Xung đột: hai khối trùng khung giờ TRÊN CÙNG địa bàn (cùng
 * mã hoặc quan hệ cha–con) được đánh dấu kèm dòng giải thích lịch nào thắng theo
 * mức ưu tiên, cùng quy tắc `diaBanTrungNhau`/`THU_TU_UU_TIEN` mà máy chủ dùng ở
 * `phat-hien-trung.ts` (tạo trước thắng khi hòa mức ưu tiên).
 *
 * Tệp này cũng xuất `dungDanhSachDiaBan`, `OChonDiaBan`, `ngayVeGiay` dùng chung
 * cho các màn hình nhật ký/thống kê cùng nhóm.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { lichPhatApi, type LichPhatNoiBoRa } from '../api/lich-phat-api';
import { thietBiApi, type NutDiaBan, type TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi, TrangThaiRong } from '../thanh-phan/TrangThaiManHinh';
import { TruongLoc } from '../thanh-phan/MauGiaoDien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import './lich-nhat-ky.css';

const MOT_NGAY_GIAY = 86_400;
const GIO_BAT_DAU = 4;
const GIO_KET_THUC = 22;
const t = viLichNhatKy;

/* ------------------------- Tiện ích dùng chung của nhóm ------------------------- */

/** Chuyển giá trị ô ngày `YYYY-MM-DD` (giờ địa phương) sang giây Unix; `cuoiNgay` lấy 23:59:59. */
export function ngayVeGiay(ngay: string, cuoiNgay = false): number | undefined {
  if (!ngay) return undefined;
  const ms = new Date(`${ngay}T00:00:00`).getTime();
  if (!Number.isFinite(ms)) return undefined;
  return Math.floor(ms / 1000) + (cuoiNgay ? MOT_NGAY_GIAY - 1 : 0);
}

export type TaiDiaBan = (signal: AbortSignal) => Promise<NutDiaBan[]>;

const taiDiaBanMacDinh: TaiDiaBan = (signal) => thietBiApi.cayDiaBan({ cap: 'xa' }, signal);

/**
 * Danh mục xã/phường (cấp dưới tỉnh) để lọc theo địa bàn và tra tên từ mã. Máy
 * chủ đã cắt theo phạm vi được phân công. Lỗi tải danh mục không chặn màn hình:
 * ô chọn chỉ còn "Toàn tỉnh".
 */
export function dungDanhSachDiaBan(tai: TaiDiaBan = taiDiaBanMacDinh, tat = false): NutDiaBan[] {
  const [ds, datDs] = useState<NutDiaBan[]>([]);
  useEffect(() => {
    if (tat) return undefined;
    const boHuy = new AbortController();
    tai(boHuy.signal)
      .then((kq) => {
        if (!boHuy.signal.aborted) datDs([...kq].sort((a, b) => a.ten.localeCompare(b.ten, 'vi')));
      })
      .catch(() => {
        if (!boHuy.signal.aborted) datDs([]);
      });
    return () => boHuy.abort();
  }, [tai, tat]);
  return ds;
}

export function OChonDiaBan({
  id,
  nhan,
  giaTri,
  onDoi,
  ds,
  khoa = false,
}: {
  id: string;
  nhan: string;
  giaTri: string;
  onDoi: (ma: string) => void;
  ds: NutDiaBan[];
  khoa?: boolean;
}): ReactNode {
  const { khoaDiaBan } = dungPhien();
  if (khoaDiaBan) {
    // Tài khoản cấp xã: chỉ có đúng xã được phân công, không có "Toàn tỉnh".
    return (
      <TruongLoc nhan={nhan} htmlFor={id}>
        <select id={id} value={khoaDiaBan.ma} disabled>
          <option value={khoaDiaBan.ma}>{khoaDiaBan.ten}</option>
        </select>
      </TruongLoc>
    );
  }
  return (
    <TruongLoc nhan={nhan} htmlFor={id}>
      <select id={id} value={giaTri} onChange={(e) => onDoi(e.target.value)} disabled={khoa}>
        <option value="">{t.chung.toanTinh}</option>
        {/* Địa bàn đang chọn trong ngữ cảnh nhưng không có trong danh mục vẫn phải hiện được. */}
        {giaTri && !ds.some((d) => d.maDinhDanh === giaTri) ? <option value={giaTri}>{giaTri}</option> : null}
        {ds.map((d) => (
          <option key={d.maDinhDanh} value={d.maDinhDanh}>
            {d.ten}
          </option>
        ))}
      </select>
    </TruongLoc>
  );
}

/* --------------------------------- Lưới lịch tuần -------------------------------- */

export interface MH17Props {
  api?: Pick<typeof lichPhatApi, 'danhSach'>;
}

interface KhoiPhat {
  lichId: string;
  tenLichPhat: string;
  mucDoUuTien: '1' | '2' | '3';
  diaBanMa: string[];
  ngayPhat: number;
  batDau: number;
  ketThuc: number;
}

const THU_TU_UU_TIEN: Record<'1' | '2' | '3', number> = { '1': 0, '2': 1, '3': 2 };

function diaBanTrungNhau(a: string, b: string): boolean {
  return a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`);
}

function timBatDauTuan(mocNaoDo: Date): number {
  const d = new Date(Date.UTC(mocNaoDo.getUTCFullYear(), mocNaoDo.getUTCMonth(), mocNaoDo.getUTCDate()));
  const thu = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (thu - 1));
  return Math.floor(d.getTime() / 1000);
}

function toKhoi(ds: LichPhatNoiBoRa[]): KhoiPhat[] {
  const kq: KhoiPhat[] = [];
  for (const l of ds) {
    for (const n of l.danhSachNgayPhat) {
      for (const tp of n.ThoiDiemPhat) {
        kq.push({
          lichId: l.id,
          tenLichPhat: l.tenLichPhat,
          mucDoUuTien: l.mucDoUuTien,
          diaBanMa: l.phamVi.map((p) => p.DichID),
          ngayPhat: n.NgayPhat,
          batDau: tp.ThoiGianBatDau,
          ketThuc: tp.ThoiGianKetThuc,
        });
      }
    }
  }
  return kq;
}

/** Với mỗi khối, tìm khối khác trùng giờ + cùng địa bàn, và ai thắng. */
function tinhXungDot(khoi: KhoiPhat[]): Map<string, { doiThu: KhoiPhat; thang: KhoiPhat }> {
  const kq = new Map<string, { doiThu: KhoiPhat; thang: KhoiPhat }>();
  for (let i = 0; i < khoi.length; i += 1) {
    for (let j = i + 1; j < khoi.length; j += 1) {
      const a = khoi[i] as KhoiPhat;
      const b = khoi[j] as KhoiPhat;
      const trungGio = a.batDau < b.ketThuc && b.batDau < a.ketThuc;
      if (!trungGio) continue;
      const trungDiaBan = a.diaBanMa.some((x) => b.diaBanMa.some((y) => diaBanTrungNhau(x, y)));
      if (!trungDiaBan) continue;
      const aThang =
        THU_TU_UU_TIEN[a.mucDoUuTien] < THU_TU_UU_TIEN[b.mucDoUuTien] ||
        (THU_TU_UU_TIEN[a.mucDoUuTien] === THU_TU_UU_TIEN[b.mucDoUuTien] && a.lichId < b.lichId);
      const khoaA = `${a.lichId}-${a.ngayPhat}-${a.batDau}`;
      const khoaB = `${b.lichId}-${b.ngayPhat}-${b.batDau}`;
      kq.set(khoaA, { doiThu: b, thang: aThang ? a : b });
      kq.set(khoaB, { doiThu: a, thang: aThang ? a : b });
    }
  }
  return kq;
}

/**
 * Bỏ lịch đã hủy khỏi một trang kết quả. Từ khi `GET /api/v1/schedules` có tham số
 * `trangThai`, không truyền tham số này thì máy chủ trả CẢ lịch `da_huy` (trước đây
 * máy chủ luôn tự loại) — lưới lịch tuần phải tự loại để không vẽ lịch đã hủy.
 */
export function boLichDaHuy(trang: TrangDuLieu<LichPhatNoiBoRa>): TrangDuLieu<LichPhatNoiBoRa> {
  const items = trang.items.filter((l) => l.trangThai !== 'da_huy' && l.hieuLuc !== 'da_huy');
  return { ...trang, items, total: trang.total - (trang.items.length - items.length) };
}

function LuoiLichTuan({ goi }: { goi: Pick<typeof lichPhatApi, 'danhSach'> }): ReactNode {
  const phien = dungPhien();
  const [mocTuan, datMocTuan] = useState(() => timBatDauTuan(new Date()));

  const kq = dungTaiDuLieu<TrangDuLieu<LichPhatNoiBoRa>>({
    tai: async (signal) =>
      boLichDaHuy(
        await goi.danhSach(
          {
            pageSize: 200,
            nguonId: phien.nguonIdHienTai,
            // Máy chủ so sánh với cột giây Unix (`thoi_diem_phat`), nên gửi số giây,
            // không gửi chuỗi ISO (chuỗi ISO bị máy chủ bỏ qua âm thầm).
            tuThoiDiem: String(mocTuan),
            denThoiDiem: String(mocTuan + 7 * MOT_NGAY_GIAY),
            // KHÔNG truyền `trangThai=con_hieu_luc`: lưới tuần vẫn phải thấy lịch đã
            // hết hiệu lực của các ngày đã qua trong tuần; chỉ bỏ lịch đã hủy.
          },
          signal,
        ),
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: false,
    phuThuoc: [mocTuan, phien.nguonIdHienTai],
  });

  const khoi = useMemo(() => toKhoi(kq.duLieu?.items ?? []), [kq.duLieu]);
  const xungDot = useMemo(() => tinhXungDot(khoi), [khoi]);

  const ngayTrongTuan = useMemo(
    () => Array.from({ length: 7 }, (_, i) => mocTuan + i * MOT_NGAY_GIAY),
    [mocTuan],
  );

  return (
    <div data-vung="che-do-lich-tuan">
      <div className="lnk-loc">
        <button type="button" className="nut" onClick={() => datMocTuan((x) => x - 7 * MOT_NGAY_GIAY)}>
          « {vi.lichPhat.tuanTruoc}
        </button>
        <span data-vung="ten-tuan">
          {new Date(mocTuan * 1000).toLocaleDateString('vi-VN')} –{' '}
          {new Date((mocTuan + 6 * MOT_NGAY_GIAY) * 1000).toLocaleDateString('vi-VN')}
        </span>
        <button type="button" className="nut" onClick={() => datMocTuan((x) => x + 7 * MOT_NGAY_GIAY)}>
          {vi.lichPhat.tuanSau} »
        </button>
        <Link to="/lich-phat/moi" className="nut nut--chinh">
          + {t.chung.themMoiLich}
        </Link>
      </div>

      {kq.trangThai === 'dang-tai' ? <KhungXuong soDong={8} soCot={7} taiLau={kq.taiLau} /> : null}
      {kq.trangThai === 'loi' && kq.loi ? <TrangThaiLoi loi={kq.loi} onThuLai={kq.taiLai} /> : null}
      {kq.trangThai === 'rong' ? <TrangThaiRong moTa={vi.lichPhat.rongMoTa} /> : null}

      {kq.trangThai === 'co-du-lieu' ? (
        <table className="lich-tuan" data-vung="luoi-lich-tuan">
          <thead>
            <tr>
              <th scope="col">{t.lichTuan.gio}</th>
              {ngayTrongTuan.map((n) => (
                <th scope="col" key={n}>
                  {new Date(n * 1000).toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit' })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: GIO_KET_THUC - GIO_BAT_DAU }, (_, i) => GIO_BAT_DAU + i).map((gio) => (
              <tr key={gio}>
                <th scope="row">{String(gio).padStart(2, '0')}:00</th>
                {ngayTrongTuan.map((ngay) => {
                  const cacKhoi = khoi.filter(
                    (k) => k.ngayPhat === ngay && Math.floor((k.batDau - ngay) / 3600) === gio,
                  );
                  return (
                    <td key={ngay} data-o-gio={`${ngay}-${gio}`}>
                      {cacKhoi.map((k) => {
                        const xd = xungDot.get(`${k.lichId}-${k.ngayPhat}-${k.batDau}`);
                        return (
                          <div
                            key={`${k.lichId}-${k.batDau}`}
                            className={`lich-tuan__khoi lich-tuan__khoi--${xd ? 'xung-dot' : 'binh-thuong'}`}
                            data-xung-dot={Boolean(xd)}
                          >
                            <span>{k.tenLichPhat}</span>
                            {xd ? (
                              <p role="alert" data-vung="giai-thich-xung-dot">
                                {dienVao(vi.lichPhat.xungDotGiaiThich, {
                                  ten: xd.doiThu.tenLichPhat,
                                  uuTien: xd.thang.mucDoUuTien,
                                  thang: xd.thang.tenLichPhat,
                                })}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/* ------------------------------------- Màn hình ------------------------------------ */

export function MH17LichTuan({ api }: MH17Props = {}): ReactNode {
  const goi = api ?? lichPhatApi;
  const phien = dungPhien();

  return (
    <div className="man-hinh" data-man-hinh="MH-17">
      <div className="man-hinh__dau">
        <h1>{t.lichTuan.tieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          {phien.coQuyen('broadcast.relay') ? (
            <Link to="/tiep-song" className="nut">
              {vi.phatSong.tiepSongTieuDe}
            </Link>
          ) : null}
          {phien.coQuyen('broadcast.live') ? (
            <Link to="/phat-song/truc-tiep" className="nut">
              {vi.phatSong.trucTiepTieuDe}
            </Link>
          ) : null}
        </div>
      </div>

      <div className="lnk-che-do">
        <Link to="/lich-phat" data-hanh-dong="xem-dang-danh-sach">
          {t.lichTuan.xemDangDanhSach}
        </Link>
      </div>

      <LuoiLichTuan goi={goi} />
    </div>
  );
}
